import { useState, useCallback } from 'react';
import { createLookup } from '../api/lookups';
import type { CreateLookupRequest, LookupDto } from '../types/lookup';

interface UseCreateLookupResult {
  create: (data: CreateLookupRequest) => Promise<LookupDto>;
  isLoading: boolean;
  error: string | null;
  reset: () => void;
}

/**
 * Hook for creating a new lookup item
 */
export function useCreateLookup(
  type: string,
  onSuccess?: (lookup: LookupDto) => void
): UseCreateLookupResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useCallback(
    async (data: CreateLookupRequest): Promise<LookupDto> => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await createLookup(type, data);
        onSuccess?.(result);
        return result;
      } catch (err) {
        const apiError = err as { response?: { data?: { message?: string } } };
        const message = apiError.response?.data?.message || 'Failed to create item';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [type, onSuccess]
  );

  const reset = useCallback(() => {
    setError(null);
  }, []);

  return {
    create,
    isLoading,
    error,
    reset,
  };
}
